import { useCallback, useEffect, useState } from 'react';
import { ErreurApi } from '../lib/apiClient';

/**
 * Retarde la propagation d'une valeur qui change vite.
 *
 * La recherche se rafraichit a la frappe : sans ce delai, chaque touche
 * declencherait une requete, et les reponses pourraient revenir dans le
 * desordre. 300 ms est assez court pour paraitre immediat et assez long
 * pour ne pas interroger le serveur a chaque lettre.
 */
export function useDelai<T>(valeur: T, millisecondes = 300): T {
  const [retardee, setRetardee] = useState(valeur);

  useEffect(() => {
    const minuteur = setTimeout(() => setRetardee(valeur), millisecondes);
    return () => clearTimeout(minuteur);
  }, [valeur, millisecondes]);

  return retardee;
}

/**
 * Chargement d'une ressource distante avec ses trois etats. `cle` sert de
 * declencheur : changer d'entite active doit relancer tous les
 * chargements, sans quoi l'ecran afficherait les donnees de l'entite
 * precedente — exactement la confusion que le CDC cherche a eviter.
 */
export function useRessource<T>(
  charger: () => Promise<T>,
  dependances: unknown[] = [],
): {
  donnees: T | null;
  chargement: boolean;
  erreur: string | null;
  recharger: () => void;
} {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteur, setCompteur] = useState(0);

  const recharger = useCallback(() => setCompteur((valeur) => valeur + 1), []);

  useEffect(() => {
    let annule = false;
    setChargement(true);
    setErreur(null);

    charger()
      .then((resultat) => {
        if (!annule) setDonnees(resultat);
      })
      .catch((cause: unknown) => {
        if (annule) return;
        setErreur(
          cause instanceof ErreurApi ? cause.message : 'Impossible de charger ces donnees.',
        );
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });

    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependances, compteur]);

  return { donnees, chargement, erreur, recharger };
}
